import {
  LiveEventTypeEnum,
  LiveEventAudienceEnum,
} from "@/__generated__/types";
import { useMemo } from "react";
import { getEventTypeLabel } from "../../CommunityToolsOverview/GamesLiveEventsTab/utils";
import { useTranslation } from "react-i18next";
import { ChatChannel } from "@/__generated__/graphql";

export function useLiveEventFilterOptions(
  availableChatChannels: ChatChannel[],
) {
  const { t } = useTranslation();

  const filterOptions = useMemo(() => {
    const eventTypeValues = (Object.values(LiveEventTypeEnum) as string[]).map(
      (value) => ({
        value,
        renderComponent: ({ value: v }: { value: string }) =>
          getEventTypeLabel(v as LiveEventTypeEnum),
      }),
    );

    const audienceLabel = (v: LiveEventAudienceEnum) => {
      switch (v) {
        case LiveEventAudienceEnum.Everyone:
          return t("gamesAndLiveEvents.table.audience.everyone");
        case LiveEventAudienceEnum.Applicants:
          return t("gamesAndLiveEvents.table.audience.applicants");
        case LiveEventAudienceEnum.Admits:
          return t("gamesAndLiveEvents.table.audience.admits");
        case LiveEventAudienceEnum.Commits:
          return t("gamesAndLiveEvents.table.audience.commits");
        case LiveEventAudienceEnum.Other:
          return t("gamesAndLiveEvents.table.audience.other");
        default:
          return String(v);
      }
    };

    const audienceValues = (
      Object.values(LiveEventAudienceEnum) as string[]
    ).map((value) => ({
      value,
      renderComponent: ({ value: v }: { value: string }) =>
        audienceLabel(v as LiveEventAudienceEnum),
    }));

    const chatChannelValues = availableChatChannels.map(
      ({ id, displayName }) => ({
        value: id,
        renderComponent: () => displayName,
      }),
    );

    return [
      {
        id: "eventType",
        label: t("gamesLiveEvents.communityToolsOverview.filters.eventType"),
        values: eventTypeValues,
      },
      {
        id: "audience",
        label: t("gamesLiveEvents.communityToolsOverview.filters.audience"),
        values: audienceValues,
      },
      {
        id: "chatChannel",
        label: t("gamesLiveEvents.communityToolsOverview.filters.chatChannel"),
        values: chatChannelValues,
      },
    ];
  }, [t, availableChatChannels]);

  return filterOptions;
}
